import React from 'react';
import { Tabs, Form, Input, Button, Select, Table, Tag, message, Spin } from 'antd';
import { UserOutlined, EditOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useHistory } from 'react-router-dom';

import MainLayout from '../../components/Layout/MainLayout';
import { useAuth } from '../../context/AuthContext';
import { useApi } from '../../hooks/useApi';
import { formatPrice } from '../../data/helpers';

import {
  PageWrapper,
  PageHeader,
  PageTitle,
  UserGreeting,
  TabContent,
  FormWrapper,
  TableHeader,
} from './styles';

const { Option } = Select;

const CR_PROVINCES = [
  'San José',
  'Alajuela',
  'Cartago',
  'Heredia',
  'Guanacaste',
  'Puntarenas',
  'Limón',
];

function ProfileTab({ user }) {
  const [form] = Form.useForm();

  const handleSave = () => {
    form.validateFields().then(() => {
      message.success('Perfil actualizado exitosamente');
    }).catch(() => {});
  };

  const nameParts = (user.name || '').split(' ');
  const firstName = nameParts.slice(0, 2).join(' ');
  const lastName = nameParts.slice(2).join(' ');

  return (
    <TabContent>
      <FormWrapper>
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            nombre: firstName,
            apellido: lastName,
            email: user.email,
            telefono: '',
            direccion: user.address?.street || '',
            provincia: user.address?.province || '',
            ciudad: user.address?.city || '',
          }}
        >
          <Form.Item name="nombre" label="Nombre" rules={[{ required: true }]}>
            <Input prefix={<UserOutlined />} />
          </Form.Item>
          <Form.Item name="apellido" label="Apellido" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="email" label="Correo electrónico" rules={[{ required: true, type: 'email' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="telefono" label="Teléfono">
            <Input />
          </Form.Item>
          <Form.Item name="direccion" label="Dirección">
            <Input />
          </Form.Item>
          <Form.Item name="provincia" label="Provincia">
            <Select>
              {CR_PROVINCES.map((prov) => (
                <Option key={prov} value={prov}>{prov}</Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="ciudad" label="Ciudad">
            <Input />
          </Form.Item>
          <Form.Item>
            <Button type="primary" onClick={handleSave}>
              Guardar cambios
            </Button>
          </Form.Item>
        </Form>
      </FormWrapper>
    </TabContent>
  );
}

function PurchasesTab({ userId }) {
  const { data: purchases, loading } = useApi(`/users/${userId}/purchases`);

  const columns = [
    { title: 'Fecha', dataIndex: 'date', key: 'date' },
    { title: 'Libro', dataIndex: 'bookTitle', key: 'bookTitle' },
    {
      title: 'Total',
      dataIndex: 'total',
      key: 'total',
      render: (total) => formatPrice(total),
    },
    {
      title: 'Estado',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={status === 'Entregado' ? 'green' : 'blue'}>{status}</Tag>
      ),
    },
  ];

  if (loading) return <Spin style={{ display: 'block', margin: '40px auto' }} />;

  return (
    <TabContent>
      <Table
        dataSource={purchases || []}
        columns={columns}
        rowKey="id"
        pagination={false}
      />
    </TabContent>
  );
}

function SalesTab({ userId }) {
  const { data: sales, loading } = useApi(`/users/${userId}/sales`);

  const columns = [
    { title: 'Fecha', dataIndex: 'date', key: 'date' },
    { title: 'Libro', dataIndex: 'bookTitle', key: 'bookTitle' },
    { title: 'Comprador', dataIndex: 'buyerDisplayName', key: 'buyerDisplayName' },
    {
      title: 'Total',
      dataIndex: 'total',
      key: 'total',
      render: (total) => formatPrice(total),
    },
    {
      title: 'Estado',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={status === 'Entregado' ? 'green' : status === 'Enviado' ? 'blue' : 'orange'}>
          {status}
        </Tag>
      ),
    },
  ];

  if (loading) return <Spin style={{ display: 'block', margin: '40px auto' }} />;

  return (
    <TabContent>
      <Table
        dataSource={sales || []}
        columns={columns}
        rowKey="id"
        pagination={false}
      />
    </TabContent>
  );
}

function ProductsTab({ sellerId }) {
  const { data: listings, loading } = useApi(sellerId ? `/listings?sellerId=${sellerId}` : null);

  const columns = [
    { title: 'Libro', dataIndex: 'bookTitle', key: 'bookTitle' },
    {
      title: 'Precio',
      dataIndex: 'price',
      key: 'price',
      render: (price) => formatPrice(price),
    },
    { title: 'Condición', dataIndex: 'condition', key: 'condition' },
    { title: 'Disponibles', dataIndex: 'copiesAvailable', key: 'copiesAvailable' },
    {
      title: 'Acciones',
      key: 'actions',
      render: () => (
        <span>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => message.info('Editar producto (próximamente)')}
          >
            Editar
          </Button>
          <Button
            type="link"
            danger
            icon={<DeleteOutlined />}
            onClick={() => message.info('Eliminar producto (próximamente)')}
          >
            Eliminar
          </Button>
        </span>
      ),
    },
  ];

  if (loading) return <Spin style={{ display: 'block', margin: '40px auto' }} />;

  return (
    <TabContent>
      <TableHeader>
        <div />
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => message.info('Agregar producto (próximamente)')}
        >
          Agregar producto
        </Button>
      </TableHeader>
      <Table
        dataSource={listings || []}
        columns={columns}
        rowKey="id"
        pagination={false}
      />
    </TabContent>
  );
}

function PaymentTab() {
  const [form] = Form.useForm();

  const handleSave = () => {
    form.validateFields().then(() => {
      message.success('Información de pago actualizada exitosamente');
    }).catch(() => {});
  };

  return (
    <TabContent>
      <FormWrapper>
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            tipoTarjeta: 'visa',
            numeroTarjeta: '**** **** **** 4532',
            fechaVencimiento: '12/28',
            cvv: '***',
          }}
        >
          <Form.Item name="tipoTarjeta" label="Tipo de tarjeta">
            <Select>
              <Option value="visa">Visa</Option>
              <Option value="mastercard">Mastercard</Option>
              <Option value="amex">American Express</Option>
            </Select>
          </Form.Item>
          <Form.Item name="numeroTarjeta" label="Número de tarjeta">
            <Input />
          </Form.Item>
          <Form.Item name="fechaVencimiento" label="Fecha de vencimiento">
            <Input />
          </Form.Item>
          <Form.Item name="cvv" label="CVV">
            <Input />
          </Form.Item>
          <Form.Item>
            <Button type="primary" onClick={handleSave}>
              Guardar cambios
            </Button>
          </Form.Item>
        </Form>
      </FormWrapper>
    </TabContent>
  );
}

export default function Account() {
  const { user, loading: authLoading } = useAuth();
  const history = useHistory();

  if (authLoading) {
    return (
      <MainLayout showSidebar={false}>
        <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />
      </MainLayout>
    );
  }

  if (!user) {
    history.push('/ingresar');
    return null;
  }

  const tabItems = [
    {
      key: 'perfil',
      label: 'Perfil',
      children: <ProfileTab user={user} />,
    },
    {
      key: 'compras',
      label: 'Mis Compras',
      children: <PurchasesTab userId={user.id} />,
    },
    ...(user.isSeller
      ? [
          {
            key: 'ventas',
            label: 'Mis Ventas',
            children: <SalesTab userId={user.id} />,
          },
          {
            key: 'productos',
            label: 'Mis Productos',
            children: <ProductsTab sellerId={user.sellerId} />,
          },
        ]
      : []),
    ...(user.isBuyer
      ? [
          {
            key: 'favoritos',
            label: 'Favoritos',
            children: <TabContent><div>Favoritos próximamente</div></TabContent>,
          },
        ]
      : []),
    {
      key: 'pago',
      label: 'Pago',
      children: <PaymentTab />,
    },
  ];

  return (
    <MainLayout showSidebar={false}>
      <PageWrapper>
        <PageHeader>
          <PageTitle>Mi Cuenta</PageTitle>
          <UserGreeting>Bienvenida, {user.name}</UserGreeting>
        </PageHeader>

        <Tabs items={tabItems} size="large" />
      </PageWrapper>
    </MainLayout>
  );
}
